import {
  ApolloClient,
  ApolloLink,
  FieldPolicy,
  fromPromise,
  InMemoryCache,
  Reference,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

// Reusable merge for cursor-based paginated fields using { edges, pageInfo, totalCount }.
// Appends incoming edges when paginating (args.after present), replaces on fresh query.
function cursorPaginatedField(keyArgs: string[] | false): FieldPolicy {
  return {
    keyArgs,
    merge(existing, incoming, { args }) {
      if (!existing || !args?.after) return incoming;
      return {
        ...incoming,
        edges: [...(existing.edges ?? []), ...(incoming.edges ?? [])],
      };
    },
  };
}

export function createApolloClient(graphqlEndpoint: string, wsEndpoint?: string) {
  const uploadLink = createUploadLink({
    uri: graphqlEndpoint,
    credentials: 'include', // ✅ Send httpOnly cookies with every request (Phase 2 security)
  }) as unknown as ApolloLink;

  // Keep authLink for backward compatibility during migration (users with old localStorage tokens)
  // Backend supports both cookie and Authorization header during transition period
  const authLink = setContext((_, { headers }) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  // Refresh-token rotation: when the access JWT (15m TTL) expires, the
  // backend returns "not authenticated". This link transparently calls the
  // refreshToken mutation (which uses the long-lived go2fix_refresh cookie)
  // and retries the original request. A single in-flight refresh is shared
  // across concurrent failed requests so we don't burn through tokens.
  let refreshing: Promise<boolean> | null = null;
  const refreshOnce = (): Promise<boolean> => {
    if (refreshing) return refreshing;
    refreshing = fetch(graphqlEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operationName: 'RefreshTokenSilent',
        query: 'mutation RefreshTokenSilent { refreshToken { user { id } } }',
      }),
    })
      .then(async (res) => {
        if (!res.ok) return false;
        const body = await res.json().catch(() => null);
        return !!body && !body.errors;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  };

  const refreshLink = onError(({ graphQLErrors, operation, forward }) => {
    if (!graphQLErrors || graphQLErrors.length === 0) return;
    if (operation.operationName === 'RefreshTokenSilent') return;
    const isAuthError = graphQLErrors.some(
      (e) =>
        e.message === 'not authenticated' ||
        e.message.startsWith('session expired'),
    );
    if (!isAuthError) return;
    return fromPromise(refreshOnce()).flatMap((ok) =>
      ok ? forward(operation) : forward(operation),
    );
  });

  let link = ApolloLink.from([refreshLink, authLink, uploadLink]);

  // Phase 4: Auto-detect WebSocket protocol from HTTP endpoint
  // If no wsEndpoint provided, derive it from graphqlEndpoint
  // https:// → wss://, http:// → ws://
  if (!wsEndpoint) {
    try {
      const url = new URL(graphqlEndpoint);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      wsEndpoint = `${wsProtocol}//${url.host}${url.pathname}`;
    } catch (e) {
      console.warn('Failed to auto-detect WebSocket endpoint:', e);
    }
  }

  if (wsEndpoint) {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsEndpoint,
        connectionParams: () => {
          const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      }),
    );

    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      ApolloLink.from([refreshLink, authLink, uploadLink]),
    );
  }

  return new ApolloClient({
    link,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Cursor-based paginated queries — proper merge for fetchMore + separate cache per filter
            myBookings: cursorPaginatedField(['status']),
            myInvoices: cursorPaginatedField(false),
            myPaymentHistory: cursorPaginatedField(false),
            companyBookings: cursorPaginatedField(['status']),
            companyInvoices: cursorPaginatedField(['status']),
            allBookings: cursorPaginatedField(['status', 'companyId']),
            allInvoices: cursorPaginatedField(['type', 'status', 'companyId']),
            searchCompanies: cursorPaginatedField(['query', 'status']),
          },
        },
        ChatRoom: {
          fields: {
            messages: {
              // Deduplicate messages by __ref (normalized) or id (inline) to prevent
              // race conditions between sendMessage mutation update and subscription onData.
              merge(existing, incoming) {
                if (!existing) return incoming;
                const existingRefs = new Set(
                  (existing.edges ?? []).map(
                    (e: Reference | { id: string }) => ('__ref' in e ? e.__ref : e.id),
                  ),
                );
                const newEdges = (incoming.edges ?? []).filter(
                  (e: Reference | { id: string }) =>
                    !existingRefs.has('__ref' in e ? e.__ref : e.id),
                );
                return {
                  ...incoming,
                  edges: [...existing.edges, ...newEdges],
                };
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
}
